import { useContext, useEffect } from 'react';

import { BasketContext } from './BasketProvider';

export function useBasket() {
  const context = useContext(BasketContext);
  if (!context) {
    throw new Error('useBasket must be used within BasketProvider');
  }
  return context;
}

// Non-throwing basket access for components that render both inside and outside the manual-booking basket provider
// (e.g. the shared header cart badge) — returns null when there is no basket in scope.
export function useBasketOptional() {
  return useContext(BasketContext);
}

/**
 * Declares that checkout is on screen. Called by the checkout body itself rather than matched on a route, because it
 * renders both at the basket route and inline as the final search step, and both count as having reached checkout.
 * Unlocks the header basket shortcut for the rest of this basket's life, and suppresses it while checkout is showing.
 *
 * `enabled` is how a checkout that owns its own basket (the trip page and CRM accommodation modal) opts out: it is
 * not the app basket being paid for, so announcing it would light up a header shortcut to an unrelated basket.
 */
export function useCheckoutPresence(enabled = true): void {
  const { markCheckoutSeen, setCheckoutOnScreen } = useBasket();

  useEffect(() => {
    if (!enabled) {
      return;
    }
    markCheckoutSeen();
    setCheckoutOnScreen(true);
    return () => setCheckoutOnScreen(false);
  }, [enabled, markCheckoutSeen, setCheckoutOnScreen]);
}
